







































































































































































import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import AnxTable from "../AnxTable/AnxTable.vue";
import AnxTableRow from "../AnxTableRow/AnxTableRow.vue";
import AnxTableCol from "../AnxTableCol/AnxTableCol.vue";
import AnxTitle from "../AnxTitle/AnxTitle.vue";
import AnxIcon from "../AnxIcon/AnxIcon.vue";
import AnxModal from "../AnxModal/AnxModal.vue";
import AnxButton from "../AnxButton/AnxButton.vue";
import AnxInput from "../AnxInput/AnxInput.vue";
import { AbstractModel } from "../../lib/models/AbstractModel";

/**
 * This component is based on the *anx-table* and fully automatically renders a CRUD table with your data.
 */
@Component({
  components: {
    AnxTable,
    AnxTableRow,
    AnxTableCol,
    AnxTitle,
    AnxIcon,
    AnxModal,
    AnxButton,
    AnxInput
  }
})
export default class AnxCrudTable extends Vue {
  /** This is the model class the crud table uses to retrieve data */
  @Prop({ default: null }) modelClass!: typeof AbstractModel | null;

  /** With this property the header can be hidden */
  @Prop({ default: true }) showHeader!: boolean;

  /** Here you can provide columns that will be used for the search field. The columns have to match the properties of the model class. If no columns are provided, the search field will not be displayed */
  @Prop({ default: null }) searchColumns!: Array<string> | null;

  /** This is the text for the label of the search field */
  @Prop({ default: "Search" }) searchFieldLabel!: string;

  /** Show delete action */
  @Prop({ default: true }) deletable!: boolean;

  /** This is the text for the create button */
  @Prop({ default: "Create new" }) createButtonText!: string;

  /** Show edit action */
  @Prop({ default: true }) editable!: boolean;

  /** Authorization header for API requests */
  @Prop({ default: "" }) authorization!: string;

  /** The sorting for the table. The *name of the column* and the *order* (ASC, DESC) have to be provided as object */
  @Prop({
    default: () => {
      return { name: 0, order: "ASC" };
    }
  })
  sort!: {
    name: number;
    order: string;
  };

  /** Array of columns which should be hidden for the user */
  @Prop({ default: () => [] }) hideColumns!: string[];

  /** Define maximum number of items shown, if number of items exceeds this, pagination will be added */
  @Prop({ default: -1 }) maxItems!: number;

  instances: AbstractModel[] = [];
  selectedItem: AbstractModel | null = null;
  createInstance: AbstractModel | null = null;

  showDeleteModal = false;
  showEditModal = false;
  showCreateModal = false;
  internalSort: {
    name: number;
    order: string;
  } = { name: 0, order: "ASC" };

  searchValue = "";

  /** Number of pages should be at least 1 (even if there are no entries: "Page 1 of 1") */
  private get numberPages() {
    const num = Math.ceil(this.sortedInstances.length / this.maxItems);
    if (num <= 0) {
      return 1;
    } else {
      return num;
    }
  }

  /** _page cannot be used as name for internal page */
  page_ = 0;
  private get page(): number {
    if (this.page_ < this.numberPages) {
      return this.page_;
    } else {
      this.page = this.numberPages - 1;
      return this.page_;
    }
  }

  private set page(page: number) {
    if (page < 0 || this.numberPages <= 0) {
      this.page_ = 0;
    } else if (page >= this.numberPages && this.numberPages > 0) {
      this.page_ = this.numberPages - 1;
    } else {
      this.page_ = page;
    }
  }

  mounted() {
    this.fetch();

    if (this.sort) {
      this.internalSort = this.sort;
    }
  }

  @Watch("sort")
  onSortChanged() {
    if (this.sort) {
      this.internalSort = this.sort;
    }
  }

  @Watch("modelClass")
  onModelClassChange() {
    this.fetch();
  }

  get paginatedSortedFilteredInstancesNonSoftDeleted() {
    return this.paginatedSortedFilteredInstances.filter(instance => {
      if (
        Object.prototype.hasOwnProperty.call(instance, "deleted") &&
        (instance as any).deleted // eslint-disable-line
      ) {
        return false;
      }
      return true;
    });
  }

  get paginatedSortedFilteredInstances() {
    const instances = this.paginatedSortedInstances;
    this.hideColumns.forEach(column => {
      instances.map(instance => {
        if (Object.prototype.hasOwnProperty.call(instance, column)) {
          // eslint-disable-next-line
          delete (instance as any)[column];
        }
      });
    });
    return instances;
  }

  get paginatedSortedInstances() {
    if (!this.isPaginated) {
      return this.sortedInstances;
    }
    return this.sortedInstances.slice(
      this.page * this.maxItems,
      (this.page + 1) * this.maxItems
    );
  }

  get isPaginated() {
    return this.maxItems > 0;
  }

  get useSearch() {
    // Check if search columns are defined
    if (!this.searchColumns || this.searchColumns.length === 0) {
      return false;
    }

    // If there are no instances we can't use search
    if (!this.instances || this.instances.length === 0) {
      return false;
    }

    // Check if all search columns are right, otherwise don't use the search
    for (let i = 0; i < this.searchColumns.length; i++) {
      if (!(this.searchColumns[i] in this.instances[0])) {
        // eslint-disable-next-line
        console.error(
          `Unknown search column ${this.searchColumns[i]}! Check if you are using the property properly.`
        );
        return false;
      }
    }

    return true;
  }

  get searchFilteredInstances() {
    // Check if the search should be used
    if (!this.useSearch || !this.searchValue || this.searchValue == "") {
      return this.instances;
    }

    const instances: AbstractModel[] = [];
    this.instances.forEach((instance: AbstractModel) => {
      if (this.searchColumns) {
        for (let i = 0; i < this.searchColumns.length; i++) {
          if (this.searchColumns[i] in instance) {
            if (
              // eslint-disable-next-line
              (instance as any)[this.searchColumns[i]]
                .toLowerCase()
                .indexOf(this.searchValue.toLowerCase()) >= 0
            ) {
              instances.push(instance);
              break;
            }
          }
        }
      }
    });

    return instances;
  }

  get sortedInstances() {
    if (
      !this.internalSort ||
      !this.internalSort.name ||
      !this.internalSort.order
    ) {
      return this.searchFilteredInstances;
    }
    const sort = this.searchFilteredInstances.sort((a, b) => {
      /*eslint-disable */ 
      /**
       * We need to access the object dynamically here but 
       * we don't know the properties of it since the modelClass is injected on runtime
       */ 
      if (this.internalSort.order === "ASC") {
        return (
          (a as any)[this.internalSort.name] -
          (b as any)[this.internalSort.name]
        );
      } else {
        return (
          (b as any)[this.internalSort.name] -
          (a as any)[this.internalSort.name]
        );
      }
      /*eslint-enable */
    });
    return sort;
  }

  /**
   * Only show actions tab when there are any actions, you need to add upcoming actions here too
   */
  get hasActions() {
    return this.editable || this.deletable;
  }

  get tableColumns() {
    if (this.instances.length) {
      let instanceProps: {
        name: string;
        index?: string;
        width?: string | null;
        align?: string | null;
      }[] = Object.keys(this.instances[0]).map((v: string) => {
        return {
          name: v
        };
      });
      instanceProps = instanceProps.filter(instanceProp => {
        if (this.hideColumns.includes(instanceProp.name)) {
          return false;
        }
        return true;
      });
      if (instanceProps[0].name === "id") {
        instanceProps[0].width = "60px";
        instanceProps[0].align = "center";
      }

      if (this.hasActions) {
        instanceProps.push({
          name: "Actions"
        });
      }
      return instanceProps;
    }
    return [];
  }

  async fetch() {
    if (!this.modelClass) {
      return;
    }

    try {
      this.instances = await this.modelClass.getAll({
        authorization: this.authorization
      });
    } catch (ex) {
      this.instances = [];
    }
  }

  async deleteSelectedItem() {
    if (this.selectedItem) {
      await this.selectedItem.delete({ authorization: this.authorization });
      await this.fetch();
      this.showDeleteModal = false;
    } else {
      throw new Error("No selected item to delete set!");
    }
  }

  async editSelectedItem() {
    if (this.selectedItem) {
      await this.selectedItem.update({ authorization: this.authorization });
      await this.fetch();
      this.showEditModal = false;
    } else {
      throw new Error("No selected item to update!");
    }
  }

  async createItem() {
    if (!this.createInstance) {
      return;
    }
    await this.createInstance.save({ authorization: this.authorization });
    await this.fetch();
    this.showCreateModal = false;
  }
}
